/**
 * Constants for Email Templates functionality
 */
import { ENV } from "../_helpers/EnvHelper";

export const CategoriesDefault = 'default'
export const S3_MAIL_TEMPLATE_NAME = 'MAIL_TEMPLATE';
// Email purposes list
export const EMAIL_PURPOSES = [
  "Announce",
  "Newsletter",
  "Invite to event",
  "Sell products",
  "Sell services",
  "Thank you",
  "Welcome",
  "Seasonal",
];

export const FONT_LIST = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Roboto, sans-serif', label: 'Roboto' }
]

// Tag color palette
export const TAG_COLOR_PALETTE = [
  '#D9E3F0', '#F47373', '#697689', '#37D67A', '#2CCCE4', 
  '#555555', '#dce775', '#ff8a65', '#ba68c8', '#22194D'
];

// S3 related constants
export const S3_CONFIG = {
  MAX_FILE_SIZE: ENV().REACT_APP_MAX_FILE_SIZE, // 5MB
  FILE_TYPES: {
    PNG: 'image/png'
  }
};

// File defaults
export const FILE_DEFAULTS = {
  DEFAULT_FILENAME: (timestamp: number = Date.now()) => `image-${timestamp}.png`,
  DEFAULT_QUALITY: 0.85,
  IMAGE_TYPE: 'image/png'
};

// Preview image generation
export const PREVIEW_CONFIG = {
  WIDTH: 600,
  PIXEL_RATIO: 1.5,
  QUALITY: 1.0,
  BACKGROUND_COLOR: '#FFFFFF',
  TIMEOUT: 5000, // 5 seconds
  IFRAME_WAIT: 3000, // 3 seconds
};

// Iframe config for preview generation
export const IFRAME_STYLE = {
  WIDTH: '600px',
  HEIGHT: '800px',
  POSITION: 'absolute',
  TOP: '-9999px',
  BORDER: 'none'
};

// Merge tag styles
export const MERGE_TAG_STYLES = {
  COMMON: {
    BACKGROUND_COLOR: 'rgb(232, 224, 254)',
    COLOR: 'rgb(51, 51, 51)',
    FONT_WEIGHT: '400',
    FONT_SIZE: '12px',
    LINE_HEIGHT: '16.8px',
  },
  SHORT_URL: {
    MARGIN_TOP: '6px',
    BORDER_RADIUS: '6px',
    PADDING: '4px 8px',
  },
  OPT_OUT: {
    SPAN: {
      MARGIN_TOP: '6px',
      BORDER_RADIUS: '6px',
      PADDING: '4px 8px',
    },
    LINK: {
      DISPLAY: 'inline-flex',
      ALIGN_ITEMS: 'center',
      VERTICAL_ALIGN: 'middle',
      BORDER_RADIUS: '6px',
      PADDING: '0px 6px',
      MARGIN: '2.5px 2px',
      HEIGHT: '25px',
      WHITE_SPACE: 'nowrap',
    }
  }
};

// Email Editor Constants
export const EMAIL_EDITOR = {
  ELEMENT_IDS: {
    EDITOR_CONTAINER: 'custom-email-editor-container',
    VISUAL_EDIT_MODE: 'VisualEditorEditMode'
  },
  TIMEOUTS: {
    INITIAL_STYLE_INJECTION: 1000,
    FORM_VALUES_SETUP: 100,
  },
  RETRY: {
    MAX_RETRIES: 5,
    INTERVAL: 1000
  },
  CUSTOM_STYLES: `
    .text-toolbar, .formatting-toolbar { 
      display: none !important; 
    }
    .easy-email-extensions-emailToolItem[title="Delete"]{
      position: absolute !important;
      left: 50px !important;
      top: 13% !important;
    }
    .easy-email-extensions-emailToolItem[title="Copy"],
    .easy-email-extensions-emailToolItem[title="Select parent block"],
    .easy-email-extensions-emailToolItem[title="Remove format"],
    .easy-email-extensions-emailToolItem[title="Unlink"] {
      display: none !important;
    }
    
    #Tools {
      justify-content: flex-end !important;
    }
    #Tools div div:first-child span {
      display: flex !important;
      position: absolute !important;
      left: 10px !important;
      top: 25% !important;
    }
  `
};

// Email Template List Constants
export const EMAIL_TEMPLATE_LIST = {
  PAGINATION: {
    DEFAULT_PAGE: 1,
    DEFAULT_LIMIT: 10,
    ITEMS_PER_PAGE: 10,
    INITIAL_DISPLAY_COUNT: 4,
    LOADING_DELAY: 300,
    ITEMS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
  },
  TAG_DISPLAY: {
    MAX_VISIBLE_TAGS: 2,
    MORE_TAGS_BACKGROUND: '#f0f0f0',
    MORE_TAGS_COLOR: '#666'
  },
  TAG_STYLE: {
    PADDING: '4px 8px',
    BORDER_RADIUS: '50px',
    FONT_SIZE: '0.85em',
    LINE_HEIGHT: '1'
  },
  COLORS: {
    LUMINANCE_THRESHOLD: 0.5,
    DEFAULT_DARK: '#000000',
    DEFAULT_LIGHT: '#ffffff'
  },
};

// Form Template Constants
export const FORM_TEMPLATE = {
  MAX_VISIBLE_TAGS: 2,
  TAG_STYLE: {
    FLEX_SHRINK: 0,
    MARGIN_RIGHT: "5px"
  }
};

// Tag Modal Constants
export const TAG_MODAL = {
  DEFAULT_COLOR: '#7bdcb5',
  MAX_TAG_LENGTH: 25,
  STYLES: {
    LABEL: {
      color: '#5F6276',
      fontSize: '14px',
      fontWeight: '500',
    },
    HINT_TEXT: {
      color: '#a1a5b7',
      fontSize: '12px',
      fontWeight: '500',
      textAlign: 'left' as const,
    },
  }
};
// Email mode constants for JsonToMjml
export const EMAIL_MODE = {
  PRODUCTION: 'production' as const,
  TESTING: 'testing' as const
};

// Email Template Page Constants
export const EMAIL_TEMPLATE_PAGE = {
  SEARCH: {
    DEBOUNCE_TIME: 800 // milliseconds
  },
  PAGINATION: {
    DEFAULT_PAGE: 1,
    DEFAULT_LIMIT: 10,
  },
  LOADING: {
    DELAY: 300, // milliseconds for template loading
    COMPLETION_DELAY: 400 // milliseconds to hide loading state
  }
};

// Browser-compatible hash function (simple)
export const hashData = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return hash.toString(16);
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Convert to hex string and ensure it's positive
  return (hash >>> 0).toString(16);
};
